import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useRelease } from "../../../apis/release/ReleaseProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import AlertOverlay from "../../../components/Alert";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, IconButton } from "@mui/material";
import { FaEye } from "react-icons/fa";
import { Content } from "../../Release/types";
import DocumentPreviewModal from "../../../components/DocumentPreviewModal";
import { useDocumentPreview } from "../../../hooks/useDocumentPreview";
import { getPreviewKind } from "../../../utils/documentPreview";

const ViewReleaedById: React.FC = () => {
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { release, fetchReleaseById, excluiRelease, downloadReleaseContent, excluiReleaseCargo } = useRelease();
    const { previewState, openPreview, closePreview } = useDocumentPreview();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const navItems: NavBarItem[] = [
        { label: "Home", route: "/", iconName: "house-door" },
        { label: "Pendind to release ", route: "/release", iconName: "table" },
        { label: "Released List", route: "/release/released", iconName: "table" },
    ];

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadDestination = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                await fetchReleaseById(Number(id));
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadDestination();
    }, [id, fetchReleaseById]);

    const cargoColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "", 
            width: 80, 
            renderCell: (params) => (
                <span
                    className="bi bi-trash"
                    style={{ color: "red", cursor: "pointer" }}
                    onClick={() => handleDeleteCargoRelation(Number(params.id)) }>
                </span>
            ),
        },
        { 
            field: "supplier", 
            headerName: "Supplier", 
            width: 350,
            valueGetter: (param: any) => {
                return param.name
            }
         },
        { field: "dawb", headerName: "Dawb", width: 200 },
        { field: "wrNumber", headerName: "Wr Número", width: 200 },
    ];
    
    const handleDelete = async () => {
        if (!id) return;

        try {
            const response = await excluiRelease(Number(id));

            if (!response.messages) {
                const successMessage = getSuccessMessage("deleteRelease", { id: id });
                navigate(`/release/released`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                let errorMessage = getErrorMessage("deleteReleaseError", { id: id, });
                if (response.messages.length > 0) {
                    errorMessage = response.messages.join(", ")
                }
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };
    
    const handleDeleteCargoRelation = async (cargoId: number) => {
        if (!id) return;

        try {
            const response = await excluiReleaseCargo(Number(id), cargoId);

            if (!response.messages) {
                const successMessage = getSuccessMessage("deleteRelease", { id: id });
                setAlert({ message: successMessage, variant: "success" });
                await fetchReleaseById(Number(id));
            }
            else {
                let errorMessage = getErrorMessage("deleteReleaseError", { id: id, });
                if (response.messages.length > 0) {
                    errorMessage = response.messages.join(", ")
                }
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };

    const download = async (content: Content) => {
        try {
            await downloadReleaseContent(content);
        } catch (error) {
            setAlert({ message: `Falha ao tentar baixar o arquivo ${content.description}`, variant: "danger" });
        }
    }

    // E5: visualizar PDF/imagem direto no navegador, sem baixar antes.
    const handleView = async (content: Content) => {
        try {
            await openPreview(content);
        } catch (error) {
            setAlert({ message: `Falha ao tentar visualizar o arquivo ${content.description}`, variant: "danger" });
        }
    }

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !release) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{getPageName("viewRelease")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group>
                            <Form.Label>ID</Form.Label>
                            <Form.Control size="sm" readOnly value={release.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="consignee">
                            <Form.Label>Consignee</Form.Label>
                            <Form.Control
                                type="text"
                                name="consignee"
                                value={release.consignee.name}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="destination">
                            <Form.Label>Destination</Form.Label>
                            <Form.Control
                                type="text"
                                name="destination"
                                value={release.destination.description}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="freighttype">
                            <Form.Label>Freight Type</Form.Label>
                            <Form.Control
                                type="text"
                                name="freighttype"
                                value={release.freightType.description}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="obs">
                            <Form.Label>Obs</Form.Label>
                            <Form.Control
                                type="text"
                                name="obs"
                                value={release.obs}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="shippedby">
                            <Form.Label>Shipped By</Form.Label>
                            <Form.Control
                                type="text"
                                name="shippedby"
                                value={release.shippedBy}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="to">
                            <Form.Label>To</Form.Label>
                            <Form.Control
                                type="text"
                                name="to"
                                value={release.to.identification ? `${release.to.identification} - ${release.to.name}` : release.to.name}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="releasedby">
                            <Form.Label>Released By</Form.Label>
                            <Form.Control
                                type="text"
                                name="releasedby"
                                value={release.releasedBy.username}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="released">
                            <Form.Label>released</Form.Label>
                            <Form.Control
                                type="date"
                                name="released"
                                value={release.released?.toString().split('T')[0]}
                                readOnly={true}
                                style={{ width: "100%", backgroundColor: "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <h6>Cargos</h6>
                    <Col md={12} className="mb-3">
                        <DataGrid
                            rows={release.cargos || []}
                            columns={cargoColumns}
                            checkboxSelection={false}
                            getRowClassName={(params: any) => params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"}
                            sx={{
                                    "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                    },
                                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: "white",
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#b2d1ff",
                                },
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "14px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "14px",
                                },
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    <h6>Contents</h6>
                    <Col md={12} className="mb-3">
                        <ul style={{listStyle: "none"}}>
                            {release.releaseContents.map((releaseContent) => (
                                <li key={releaseContent.content.id} className="mb-2 d-flex align-items-center">
                                    <Box onClick={() => download(releaseContent.content)}
                                        component="span"
                                        sx={{
                                            cursor: "pointer",
                                            padding: '8px',
                                            borderRadius: '4px',
                                            userSelect: 'none',
                                            '&:hover': {
                                              backgroundColor: '#f0f0f0',
                                              color: "blue"
                                            },
                                        }}
                                    >
                                        {releaseContent.content.description}
                                    </Box>
                                    {getPreviewKind(releaseContent.content.description) && (
                                        <IconButton
                                            size="small"
                                            title={getUiInterface('clickToView')}
                                            onClick={() => handleView(releaseContent.content)}
                                        >
                                            <FaEye size={13} />
                                        </IconButton>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </Col>
                </Row>
            </Form>
            
            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />

            <DocumentPreviewModal state={previewState} onClose={closePreview} />
        </Container>
    );
};

export default ViewReleaedById;
